import React, { useState } from 'react';
import { Mail, MapPin, Linkedin, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { profileData } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => { setSubmitStatus(null); }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f3ede0]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-4">
            Get in Touch
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Let's <span className="text-gold-400">Connect</span>
          </h2>
          <p className="text-lg text-[#5c6878] max-w-2xl mx-auto">
            Interested in discussing opportunities or collaborations? I'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-2xl font-bold text-[#152846] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Contact Information</h3>
            <div className="space-y-6 mb-10">
              <a href={`mailto:${profileData.email}`} className="flex items-start gap-4 p-4 bg-[#fffdf8] rounded-xl border border-[#e6dfd0] hover:border-gold-400/30 transition-colors group" data-testid="contact-email-link">
                <div className="w-12 h-12 bg-gold-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-gold-400" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-sm text-[#8b94a3] mb-1">Email</p>
                  <p className="text-[#3e4858] font-medium group-hover:text-gold-400 transition-colors">{profileData.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4 p-4 bg-[#fffdf8] rounded-xl border border-[#e6dfd0]">
                <div className="w-12 h-12 bg-gold-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-gold-400" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-sm text-[#8b94a3] mb-1">Location</p>
                  <p className="text-[#3e4858] font-medium">{profileData.location}</p>
                </div>
              </div>
              <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-4 bg-[#fffdf8] rounded-xl border border-[#e6dfd0] hover:border-gold-400/30 transition-colors group" data-testid="contact-linkedin-link">
                <div className="w-12 h-12 bg-gold-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Linkedin className="w-6 h-6 text-gold-400" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-sm text-[#8b94a3] mb-1">LinkedIn</p>
                  <p className="text-[#3e4858] font-medium group-hover:text-gold-400 transition-colors">Connect on LinkedIn</p>
                </div>
              </a>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-900/30 text-green-400 rounded-full border border-green-800/30">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium">Available for consulting opportunities</span>
            </div>
          </div>

          <div className="bg-[#fffdf8] p-8 rounded-2xl border border-[#e6dfd0]">
            <h3 className="text-xl font-bold text-[#152846] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Send a Message</h3>
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" strokeWidth={2.5} />
                </div>
                <h4 className="text-lg font-semibold text-[#152846] mb-2">Message Sent!</h4>
                <p className="text-[#5c6878]">Thank you for reaching out. I'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-red-900/30 text-red-400 rounded-lg border border-red-800/30">
                    <AlertCircle size={18} strokeWidth={2.5} /><span className="text-sm">Failed to send message. Please try again.</span>
                  </div>
                )}
                {['name', 'email', 'subject'].map((field) => (
                  <div key={field}>
                    <label htmlFor={field} className="block text-sm font-medium text-[#5c6878] mb-2 capitalize">{field === 'name' ? 'Your Name' : field === 'email' ? 'Email Address' : 'Subject'}</label>
                    <input type={field === 'email' ? 'email' : 'text'} id={field} name={field} value={formData[field]} onChange={handleChange} required disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] text-[#152846] rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent transition-all disabled:opacity-50 placeholder-[#aab2bf]"
                      placeholder={field === 'name' ? 'John Doe' : field === 'email' ? 'john@example.com' : 'Consulting Inquiry'}
                      data-testid={`contact-${field}-input`}
                    />
                  </div>
                ))}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#5c6878] mb-2">Message</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required disabled={isSubmitting} rows={4}
                    className="w-full px-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] text-[#152846] rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent transition-all resize-none disabled:opacity-50 placeholder-[#aab2bf]"
                    placeholder="Tell me about your project or inquiry..."
                    data-testid="contact-message-input"
                  />
                </div>
                <button type="submit" disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-all duration-300 hover:shadow-lg hover:shadow-gold-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (<><Loader2 size={18} className="animate-spin" strokeWidth={2.5} />Sending...</>) : (<><Send size={18} strokeWidth={2.5} />Send Message</>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
